/**
 * The tenant's linkage switches, the Linkages tab of its Automation (Feature
 * 74, 3.2; a band on its home before): one row per platform template,
 * switched on or off for this organization, with
 * a hint naming any step whose capability no agent here binds, since every
 * run would skip it `not_bound` (D32), and a hint on a template with an
 * independent step while the organization has no webhook endpoint, which
 * the switch then refuses (D44), and a warning on a template with an empty
 * gate, naming the step it stops (Feature 72 D4); the switch still works.
 * A switch moves at once and moves back with a toast naming the agent's
 * reason when it is refused. A template that overlaps another automation
 * on here says which (Feature 74 D5, D11), and a row opens the template's
 * record panel in this organization, with its recent runs (D8).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@optum/api-client/client';
import {
  listOrganizationLinkages,
  setOrganizationLinkage,
} from '@optum/api-client/endpoints/organization-linkages';
import type { OrganizationLinkage } from '@optum/api-client/types';

import { slotText, viewerTimeZone } from '../../lib/cron.js';
import { cronReading } from '../../lib/schedule.js';
import { qk } from '../../lib/query-keys.js';
import { useRecordPanel } from '../../components/shell/record.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { Button } from '../../ui/Button.js';
import { DividedList } from '../../ui/DividedList.js';
import { Badge, Inline, Typography } from '../../ui/finixt.js';
import { Toggle } from '../../ui/Toggle.js';
import { useToast } from '../../ui/useToast.js';
import { overlapsHere } from '../automation/orgAutomations.js';
import { OverlapBadge } from '../automation/OverlapBadge.js';
import { useOrgAutomations } from '../automation/useOrgAutomations.js';
import { blockedSteps } from './gates.js';
import { isCall, isSchedule } from './start.js';

/** A refusal's message with its reason, as `webhook_required`, when the agent gave one. */
export function refusalText(error: unknown): string {
  if (!(error instanceof ApiError)) return String(error);
  const reason = error.details?.['reason'];
  return typeof reason === 'string' ? `${error.message} (${reason})` : error.message;
}

/** "1 step bound on no agent here". */
export function unboundText(steps: readonly string[]): string {
  return `${steps.length} step${steps.length === 1 ? '' : 's'} bound on no agent here`;
}

/** "1 empty gate: crm will not run". */
export function emptyGateText(emptyGates: readonly string[]): string {
  const n = emptyGates.length;
  return `${n} empty gate${n === 1 ? '' : 's'}: ${blockedSteps(emptyGates).join(', ')} will not run`;
}

/**
 * When a template starts here: when its capability completes or is called
 * (Feature 78, 4.4), or a schedule's cron in force read in the viewer's
 * timezone, whose it is, and the agent's next slot (Feature 77, 4.3).
 */
function StartHere({ row }: { row: OrganizationLinkage }) {
  if (!isSchedule(row.start)) {
    return (
      <>
        when <span className="mono">{row.first_capability_id}</span>{' '}
        {isCall(row.start) ? 'is called' : 'completes'}
      </>
    );
  }
  const timeZone = viewerTimeZone();
  const cron = row.effective_cron ?? row.start?.cron ?? '';
  const whose = row.cron_expression ? 'its own cron' : 'the template’s cron';
  const next =
    row.enabled && row.next_fire_at ? slotText(new Date(row.next_fire_at), timeZone) : null;
  return (
    <>
      {cronReading(cron, timeZone) ?? <span className="mono">{cron} UTC</span>} · runs{' '}
      <span className="mono">{row.first_capability_id}</span> · {whose}
      {next && <> · next {next}</>}
    </>
  );
}

export function TenantLinkages({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const panel = useRecordPanel();
  const toast = useToast();
  const found = useOrgAutomations(orgId);
  const key = qk.organizationLinkages(orgId);
  const linkages = useQuery({ queryKey: key, queryFn: () => listOrganizationLinkages(orgId) });

  const toggle = useMutation({
    mutationFn: ({ template, enabled }: { template: string; enabled: boolean }) =>
      setOrganizationLinkage(orgId, template, enabled),
    onMutate: async ({ template, enabled }) => {
      await qc.cancelQueries({ queryKey: key });
      const before = qc.getQueryData<OrganizationLinkage[]>(key);
      qc.setQueryData<OrganizationLinkage[]>(key, (rows) =>
        rows?.map((row) => (row.key === template ? { ...row, enabled } : row)),
      );
      return { before };
    },
    onError: (e, _vars, context) => {
      qc.setQueryData(key, context?.before);
      toast.error('Switch refused', refusalText(e));
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: qk.linkageTemplates() });
    },
  });

  return (
    <>
      {linkages.isSuccess && linkages.data.length === 0 ? (
        <EmptyBlock>No linkages on the platform yet.</EmptyBlock>
      ) : linkages.isPending ? (
        <LoadingBlock />
      ) : linkages.isError ? (
        <ErrorBlock
          message={linkages.error instanceof Error ? linkages.error.message : 'Failed to load'}
        />
      ) : (
        <div
          style={{
            borderRadius: 'var(--radius-card)',
            border: '1px solid var(--border-hairline)',
            background: 'var(--surface-card)',
          }}
        >
          <DividedList>
            {linkages.data.map((row) => (
              <li
                key={row.key}
                style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 16px' }}
              >
                <Toggle
                  checked={row.enabled}
                  aria-label={`${row.name} enabled`}
                  disabled={toggle.isPending}
                  onChange={(enabled) => toggle.mutate({ template: row.key, enabled })}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body" style={{ fontWeight: 'var(--weight-semibold)' }}>
                    {row.name}
                  </Typography>
                  <Typography variant="meta">
                    <span className="mono">{row.key}</span> · <StartHere row={row} /> ·{' '}
                    <span className="mono">{row.step_count}</span>{' '}
                    {row.step_count === 1 ? 'step' : 'steps'}
                  </Typography>
                </div>
                <Inline gap={7} align="center" wrap={false}>
                  {overlapsHere(found, row.key).length > 0 && (
                    <OverlapBadge others={overlapsHere(found, row.key)} templates={false} />
                  )}
                  {isSchedule(row.start) && <Badge tone="violet">schedule</Badge>}
                  {row.needs_webhook && <Badge tone="info">needs a webhook endpoint</Badge>}
                  {(row.empty_gates?.length ?? 0) > 0 && (
                    <Badge tone="warn">{emptyGateText(row.empty_gates ?? [])}</Badge>
                  )}
                  {row.unbound_steps.length > 0 && (
                    <span title={row.unbound_steps.join(', ')}>
                      <Badge tone="warn">{unboundText(row.unbound_steps)}</Badge>
                    </span>
                  )}
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => panel.open({ kind: 'org-linkage', id: row.key, orgId })}
                  >
                    Open
                  </Button>
                </Inline>
              </li>
            ))}
          </DividedList>
        </div>
      )}
    </>
  );
}
